import { z } from 'zod';

/**
 * Wire result of `executeUnbound`, and the body of
 * `POST /api/v4/actions/:slug/execute`: 200 when `status` is `ok`, 422 when it
 * is `error`. The route sends it field for field, so a rep-facing caller sees
 * `code` without any route change.
 */
export const ExecutionResultSchema = z.object({
  executionId: z.string(),
  correlationId: z.string(),
  status: z.enum(['ok', 'error']),
  message: z.string().optional(),
  /**
   * True when this result reuses an EXISTING ActionExecution's correlationId
   * (in-flight dedup hit, dedup_key race loser, or CAS-lost) rather than a
   * freshly minted one. Callers that persist correlationId onto a separate
   * unique-constrained row (e.g. EnrollmentItem.correlationId) must NOT
   * write it again when this is true — see queue-send.ts.
   */
  deduped: z.boolean().optional(),
  /**
   * The handler's own `HandlerResult.code`, populated ONLY on the terminal
   * error return — e.g. `CLAY_ROUTINE_START_THROTTLED`. It lets a caller
   * (lead-scanner's `fireLeadScanningExecute`) distinguish "Clay is busy, try
   * again shortly" from every other failure.
   */
  code: z.string().optional(),
});
export type ExecutionResult = z.infer<typeof ExecutionResultSchema>;

/**
 * Codes `executeUnbound` itself sets when it refuses before dispatching. Their
 * messages are built only from designed text (field labels, the action slug),
 * never from a driver or upstream error. `ACTION_NOT_FOUND` carries an internal
 * id, so a sender does not show it to a rep.
 */
export const INPUT_VALIDATION_FAILED_CODE = 'INPUT_VALIDATION_FAILED';
export const ACTION_DISABLED_CODE = 'ACTION_DISABLED';
export const ACTION_NOT_FOUND_CODE = 'ACTION_NOT_FOUND';
